import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import MainLayout from "../../components/Layout/MainLayout";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import { api } from "../../services/api";
import {
  Add,
  Edit,
  Delete,
  Notifications as NotificationsIcon,
  FilterList,
  SmartToy,
  ExpandMore,
  ExpandLess,
  Send,
  Refresh,
  Work,
  PlayArrow,
  Pause,
  Visibility,
  DeleteSweep,
  ToggleOn,
  ToggleOff,
  Block,
  AccessTime,
} from "@mui/icons-material";
import * as Icons from "@mui/icons-material";
import JobStatusBadge from "../../components/Jobs/JobStatusBadge";
import JobDetailsModal from "../Jobs/JobDetailsModal";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const TopRowLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const LiveIndicator = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #4caf50;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const SentNotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  max-height: 600px;
  overflow-y: auto;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const SentNotificationItem = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
    ${(props) =>
      props.theme?.isDark &&
      `
      box-shadow: 0 4px 12px rgba(25, 184, 136, 0.1);
    `}
  }
`;

const SentNotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const SentNotificationTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  flex: 1;
`;

const SentNotificationTime = styled.div`
  font-size: 11px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  white-space: nowrap;
`;

const SentNotificationBody = styled.div`
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SentNotificationMeta = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  font-size: 11px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const SentNotificationCustomer = styled.span`
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  ${(props) =>
    props.theme?.isDark &&
    `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const SearchBar = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${(props) =>
    getTheme(props.theme?.isDark).fonts.primary};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
    ${(props) =>
      props.theme?.isDark &&
      `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }

  &::placeholder {
    color: ${(props) =>
      getTheme(props.theme?.isDark).colors.textSecondary};
  }
`;

const NotificationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(350px, 1fr)
  );
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NotificationCard = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    ${(props) =>
      props.theme?.isDark &&
      `
      box-shadow: 0 8px 24px rgba(25, 184, 136, 0.2);
    `}
    ${(props) =>
      !props.theme?.isDark &&
      `
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    `}
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${(props) =>
      props.$accentColor ||
      getTheme(props.theme?.isDark).colors.primary};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  position: relative;
`;

const NotificationIconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
`;

const NotificationIconImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  flex-shrink: 0;
`;

const NotificationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationKey = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const NotificationMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const MetaBadge = styled.span`
  padding: 4px 8px;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.hover};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const NotificationActions = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-direction: column;
  align-items: flex-end;

  ${NotificationCard}:hover & {
    opacity: 1;
  }
`;

const ToggleActions = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  align-items: center;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const ActionButton = styled(Button)`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.xs}
    ${(props) => getTheme(props.theme?.isDark).spacing.sm};
  min-width: auto;
`;

const TriggerInfo = styled.div`
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  padding-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  border-top: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const AIBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  padding: 4px 10px;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background: linear-gradient(
    135deg,
    #667eea 0%,
    #764ba2 100%
  );
  color: white;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
`;

const AIPromptSection = styled.div`
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.hover};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-left: 3px solid #667eea;
`;

const AIPromptHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.xs}
    0;
  user-select: none;

  &:hover {
    color: #667eea;
  }
`;

const AIPromptText = styled.div`
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  line-height: 1.6;
  padding-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  border-top: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
`;

const AIModelBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary}20;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
  font-size: 10px;
  font-weight: 600;
  margin-left: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xxl};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xxl};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  opacity: 0.5;
`;

const LanguageTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const TabButtons = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  border-bottom: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  padding-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const TabButton = styled.button`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.xs}
    ${(props) => getTheme(props.theme?.isDark).spacing.sm};
  border: none;
  background: transparent;
  color: ${(props) =>
    props.$active
      ? getTheme(props.theme?.isDark).colors.primary
      : getTheme(props.theme?.isDark).colors.textSecondary};
  font-size: 12px;
  font-weight: ${(props) =>
    props.$active ? "600" : "400"};
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) =>
      props.$active
        ? getTheme(props.theme?.isDark).colors.primary
        : "transparent"};
  transition: all 0.2s ease;
  margin-bottom: -1px;

  &:hover {
    color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }
`;

const TabContent = styled.div`
  min-height: 40px;
`;

const TabTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TabBody = styled.div`
  font-size: 14px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const JobsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  max-height: 600px;
  overflow-y: auto;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const JobItem = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
    ${(props) =>
      props.theme?.isDark &&
      `
      box-shadow: 0 4px 12px rgba(25, 184, 136, 0.1);
    `}
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const JobName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  flex: 1;
`;

const JobMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  font-size: 11px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const JobActionsContainer = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  flex-wrap: wrap;
`;

const JobTypeBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  background-color: ${(props) => {
    if (props.type === "scheduled_notification")
      return "#3b82f6";
    if (props.type === "reminder") return "#10b981";
    return "#6b7280";
  }};
  color: white;
`;

const DisabledBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  background-color: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ToggleButton = styled(Button)`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.xs}
    ${(props) => getTheme(props.theme?.isDark).spacing.sm};
  min-width: auto;
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  font-size: 12px;
  white-space: nowrap;
`;

// Helper function to get localized text from notification field
const getLocalizedText = (field, locale = "az") => {
  if (!field) return "";

  // If field is a string (legacy format), return as-is
  if (typeof field === "string") {
    return field;
  }

  // If field is an object (localized format)
  if (typeof field === "object" && field !== null) {
    // Try to get text for requested locale
    if (
      field[locale] &&
      typeof field[locale] === "string"
    ) {
      return field[locale];
    }

    // Fallback order: az → en → ru
    const fallbackOrder = ["az", "en", "ru"];
    for (const fallbackLocale of fallbackOrder) {
      if (
        field[fallbackLocale] &&
        typeof field[fallbackLocale] === "string"
      ) {
        return field[fallbackLocale];
      }
    }
  }

  return "";
};

const NotificationsList = () => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedPrompts, setExpandedPrompts] = useState(
    {}
  );
  const [sentNotifications, setSentNotifications] =
    useState([]);
  const [loadingSent, setLoadingSent] = useState(false);
  const [activeTabs, setActiveTabs] = useState({});
  const [activeSentTabs, setActiveSentTabs] = useState({});
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetailsModal, setShowJobDetailsModal] =
    useState(false);
  const [
    showSimulatedTimeModal,
    setShowSimulatedTimeModal,
  ] = useState(false);
  const [selectedJobName, setSelectedJobName] =
    useState(null);
  const [simulatedTime, setSimulatedTime] = useState("");
  const [simulatedTimezone, setSimulatedTimezone] =
    useState("");
  const [runningSimulated, setRunningSimulated] =
    useState(false);

  useEffect(() => {
    fetchNotifications();
    fetchSentNotifications();
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  // Canlı yeniləmə - hər 5 saniyədə bir
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSentNotifications();
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jobs polling - hər 30 saniyədə bir
  useEffect(() => {
    const interval = setInterval(() => {
      fetchJobs();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 12,
        ...(search && { key: search }),
      };
      const response = await api.getNotifications(params);
      setNotifications(response.results || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSentNotifications = async () => {
    try {
      setLoadingSent(true);
      const response = await api.getSentNotifications({
        limit: 50,
      });
      setSentNotifications(response.results || []);
    } catch (error) {
      console.error(
        "Error fetching sent notifications:",
        error
      );
    } finally {
      setLoadingSent(false);
    }
  };

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      const response = await api.getJobs();
      setJobs(response.results || []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) {
      return "indi";
    } else if (minutes < 60) {
      return `${minutes} dəqiqə əvvəl`;
    } else if (hours < 24) {
      return `${hours} saat əvvəl`;
    } else if (days < 7) {
      return `${days} gün əvvəl`;
    } else {
      return date.toLocaleDateString("az-AZ", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (
      window.confirm(
        "Bu bildirişi silmək istədiyinizə əminsiniz?"
      )
    ) {
      try {
        await api.deleteNotification(id);
        fetchNotifications();
      } catch (error) {
        console.error(
          "Error deleting notification:",
          error
        );
        alert("Bildiriş silinərkən xəta baş verdi");
      }
    }
  };

  const handleToggleEnabled = async (
    id,
    currentEnabled,
    e
  ) => {
    e.stopPropagation();
    try {
      await api.toggleNotificationEnabled(id);
      fetchNotifications();
    } catch (error) {
      console.error(
        "Error toggling notification enabled status:",
        error
      );
      alert(
        "Bildiriş statusu dəyişdirilərkən xəta baş verdi"
      );
    }
  };

  const handleDeleteAllCustomerNotifications = async () => {
    if (
      !window.confirm(
        "Bütün müştəri bildirişlərini silmək istədiyinizə əminsiniz? Bu əməliyyat geri alına bilməz."
      )
    ) {
      return;
    }
    try {
      const result =
        await api.deleteAllCustomerNotifications();
      alert(
        result.message ||
          `${result.deletedCount} bildiriş uğurla silindi`
      );
      fetchSentNotifications();
    } catch (error) {
      console.error(
        "Error deleting all customer notifications:",
        error
      );
      alert("Bildirişlər silinərkən xəta baş verdi");
    }
  };

  const getVariantColor = (variant) => {
    const colors = {
      PRIMARY: "#1976D2",
      STANDARD: "#9E9E9E",
      SUCCESS: "#4CAF50",
      WARNING: "#FF9800",
      DANGER: "#F44336",
    };
    return colors[variant] || colors.STANDARD;
  };

  const getVariantLabel = (variant) => {
    const labels = {
      PRIMARY: "Primary",
      STANDARD: "Standart",
      SUCCESS: "Success",
      WARNING: "Warning",
      DANGER: "Danger",
    };
    return labels[variant] || variant;
  };

  const getTriggerSummary = (triggers) => {
    if (!triggers || triggers.length === 0) {
      return "Trigger yoxdur";
    }
    const summaries = triggers.map((trigger) => {
      if (trigger.type === "CUSTOMER_FUNNEL") {
        return "Funnel Trigger";
      } else if (trigger.type === "CUSTOMER_ACTION") {
        return `Action: ${trigger.actionType}`;
      } else if (trigger.type === "MANUAL") {
        return "Manual";
      }
      return "Unknown";
    });
    return summaries.join(", ");
  };

  const togglePromptExpansion = (notificationId, e) => {
    e.stopPropagation();
    setExpandedPrompts((prev) => ({
      ...prev,
      [notificationId]: !prev[notificationId],
    }));
  };

  const setActiveTab = (notificationId, locale) => {
    setActiveTabs((prev) => ({
      ...prev,
      [notificationId]: locale,
    }));
  };

  const setActiveSentTab = (sentNotificationId, locale) => {
    setActiveSentTabs((prev) => ({
      ...prev,
      [sentNotificationId]: locale,
    }));
  };

  const getActiveTab = (notificationId) => {
    return activeTabs[notificationId] || "az";
  };

  const getActiveSentTab = (sentNotificationId) => {
    return activeSentTabs[sentNotificationId] || "az";
  };

  const handleViewJobDetails = async (job) => {
    try {
      const details = await api.getJobDetails(job.name);
      setSelectedJob(details);
      setShowJobDetailsModal(true);
    } catch (err) {
      console.error("Error fetching job details:", err);
      alert("Job detallarını yükləmək mümkün olmadı");
    }
  };

  const handleStartJob = async (jobName) => {
    if (
      !window.confirm(
        `"${jobName}" job-unu indi işə salmaq istəyirsiniz?`
      )
    ) {
      return;
    }
    try {
      await api.startJob({ name: jobName });
      alert("Job uğurla işə salındı");
      fetchJobs();
    } catch (err) {
      console.error("Error starting job:", err);
      alert("Job-u işə salmaq mümkün olmadı");
    }
  };

  const handlePauseJob = async (jobName) => {
    if (
      !window.confirm(
        `"${jobName}" job-unu dayandırmaq istəyirsiniz?`
      )
    ) {
      return;
    }
    try {
      await api.pauseJob({ name: jobName });
      alert("Job uğurla dayandırıldı");
      fetchJobs();
    } catch (err) {
      console.error("Error pausing job:", err);
      alert("Job-u dayandırmaq mümkün olmadı");
    }
  };

  const handleResumeJob = async (jobName) => {
    try {
      await api.resumeJob({ name: jobName });
      alert("Job uğurla davam etdirildi");
      fetchJobs();
    } catch (err) {
      console.error("Error resuming job:", err);
      alert("Job-u davam etdirmək mümkün olmadı");
    }
  };

  const handleRunJobWithSimulatedTime = (jobName) => {
    setSelectedJobName(jobName);
    setSimulatedTime("");
    setSimulatedTimezone("");
    setShowSimulatedTimeModal(true);
  };

  const handleExecuteSimulatedTime = async () => {
    if (!simulatedTime) {
      alert("Simulated time təyin edilməlidir");
      return;
    }

    // Validate HH:mm format
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    const isoDateTimeRegex =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$/;

    if (
      !timeRegex.test(simulatedTime) &&
      !isoDateTimeRegex.test(simulatedTime)
    ) {
      alert(
        "Simulated time formatı düzgün deyil. HH:mm (məs: 19:00) və ya ISO datetime formatı istifadə edin."
      );
      return;
    }

    try {
      setRunningSimulated(true);
      await api.runJobWithSimulatedTime({
        name: selectedJobName,
        simulatedTime: simulatedTime,
        timezone: simulatedTimezone || undefined,
      });
      alert(
        `Job ${selectedJobName} simulated time ${simulatedTime}${
          simulatedTimezone ? ` (${simulatedTimezone})` : ""
        } ilə uğurla işə salındı`
      );
      setShowSimulatedTimeModal(false);
      setSimulatedTime("");
      setSimulatedTimezone("");
      setSelectedJobName(null);
      fetchJobs();
    } catch (err) {
      console.error(
        "Error running job with simulated time:",
        err
      );
      alert(
        err.response?.data?.message ||
          "Job-u simulated time ilə işə salmaq mümkün olmadı"
      );
    } finally {
      setRunningSimulated(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("az-AZ", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCronPattern = (pattern) => {
    if (!pattern) return "N/A";
    return pattern;
  };

  const filteredNotifications = notifications.filter(
    (notification) => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      const titleText =
        getLocalizedText(notification.title, "az") || "";
      const bodyText =
        getLocalizedText(notification.body, "az") || "";
      return (
        notification.key
          ?.toLowerCase()
          .includes(searchLower) ||
        titleText.toLowerCase().includes(searchLower) ||
        bodyText.toLowerCase().includes(searchLower)
      );
    }
  );

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader>
          <Title theme={theme}>
            <NotificationsIcon />
            Bildirişlər
          </Title>
          <SearchBar>
            <SearchInput
              theme={theme}
              type="text"
              placeholder="Axtarış..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button theme={theme} variant="outline">
              <FilterList />
            </Button>
          </SearchBar>
          <Button
            onClick={() =>
              navigate("/admin/notifications/new")
            }
            theme={theme}
          >
            <Add /> Yeni Bildiriş
          </Button>
        </PageHeader>

        <TopRowLayout theme={theme}>
          <Section theme={theme}>
            <SectionHeader theme={theme}>
              <SectionTitle theme={theme}>
                <Work />
                Bildiriş Jobları
              </SectionTitle>
              <Button
                theme={theme}
                variant="outline"
                onClick={fetchJobs}
                style={{
                  padding: "4px 8px",
                  minWidth: "auto",
                }}
              >
                <Refresh />
              </Button>
            </SectionHeader>
            {loadingJobs ? (
              <LoadingContainer theme={theme}>
                Yüklənir...
              </LoadingContainer>
            ) : jobs.length === 0 ? (
              <EmptyState theme={theme}>
                <EmptyIcon>
                  <Work style={{ fontSize: "inherit" }} />
                </EmptyIcon>
                <p>Job tapılmadı</p>
              </EmptyState>
            ) : (
              <JobsList theme={theme}>
                {jobs.map((job) => (
                  <JobItem
                    key={job.id || job.name}
                    theme={theme}
                  >
                    <JobHeader theme={theme}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <JobName theme={theme}>
                          {job.name}
                        </JobName>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                            marginTop: "4px",
                          }}
                        >
                          <JobTypeBadge
                            type={job.type || "other"}
                          >
                            {job.type ===
                            "scheduled_notification"
                              ? "Scheduled Notification"
                              : job.type === "reminder"
                              ? "Reminder"
                              : "Other"}
                          </JobTypeBadge>
                          <JobStatusBadge
                            status={job.status}
                          />
                        </div>
                      </div>
                    </JobHeader>
                    <JobMeta theme={theme}>
                      <div>
                        <strong>Schedule:</strong>{" "}
                        {formatCronPattern(
                          job.repeatInterval
                        )}
                      </div>
                      <div>
                        <strong>Next Run:</strong>{" "}
                        {formatDate(job.nextRunAt)}
                      </div>
                      <div>
                        <strong>Last Run:</strong>{" "}
                        {formatDate(job.lastRunAt)}
                      </div>
                      {job.failCount > 0 && (
                        <div style={{ color: "#ef4444" }}>
                          <strong>Fail Count:</strong>{" "}
                          {job.failCount}
                        </div>
                      )}
                    </JobMeta>
                    <JobActionsContainer theme={theme}>
                      <Button
                        variant="outline"
                        onClick={() =>
                          handleViewJobDetails(job)
                        }
                        theme={theme}
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                        }}
                      >
                        <Visibility fontSize="small" />{" "}
                        Detallar
                      </Button>
                      {job.status === "paused" ? (
                        <Button
                          variant="outline"
                          onClick={() =>
                            handleResumeJob(job.name)
                          }
                          theme={theme}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                          }}
                        >
                          <PlayArrow fontSize="small" />{" "}
                          Davam et
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={() =>
                            handlePauseJob(job.name)
                          }
                          theme={theme}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                          }}
                        >
                          <Pause fontSize="small" />{" "}
                          Dayandır
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() =>
                          handleStartJob(job.name)
                        }
                        theme={theme}
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                        }}
                      >
                        <PlayArrow fontSize="small" /> İndi
                        işə sal
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() =>
                          handleRunJobWithSimulatedTime(
                            job.name
                          )
                        }
                        theme={theme}
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <AccessTime fontSize="small" />
                        Simulated Time
                      </Button>
                    </JobActionsContainer>
                  </JobItem>
                ))}
              </JobsList>
            )}
          </Section>

          <Section theme={theme}>
            <SectionHeader theme={theme}>
              <SectionTitle theme={theme}>
                <Send />
                Göndərilən Bildirişlər
                <LiveIndicator />
              </SectionTitle>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  theme={theme}
                  variant="outline"
                  onClick={fetchSentNotifications}
                  style={{
                    padding: "4px 8px",
                    minWidth: "auto",
                  }}
                >
                  <Refresh />
                </Button>
                <Button
                  theme={theme}
                  variant="danger"
                  onClick={
                    handleDeleteAllCustomerNotifications
                  }
                  style={{
                    padding: "4px 8px",
                    minWidth: "auto",
                  }}
                >
                  <DeleteSweep /> Hamısını Sil
                </Button>
              </div>
            </SectionHeader>
            {loadingSent ? (
              <LoadingContainer theme={theme}>
                Yüklənir...
              </LoadingContainer>
            ) : sentNotifications.length === 0 ? (
              <EmptyState theme={theme}>
                <EmptyIcon>
                  <Send style={{ fontSize: "inherit" }} />
                </EmptyIcon>
                <p>Hələ bildiriş göndərilməyib</p>
              </EmptyState>
            ) : (
              <SentNotificationsList theme={theme}>
                {sentNotifications.map(
                  (sentNotification) => {
                    const notification =
                      sentNotification.notification;
                    const customer =
                      sentNotification.customer;
                    const sentTime =
                      sentNotification.createdAt ||
                      sentNotification.date;
                    const sentNotificationId =
                      sentNotification._id ||
                      sentNotification.id;

                    return (
                      <SentNotificationItem
                        key={sentNotificationId}
                        theme={theme}
                      >
                        <SentNotificationHeader
                          theme={theme}
                        >
                          <div
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            <LanguageTabs theme={theme}>
                              <TabButtons theme={theme}>
                                <TabButton
                                  theme={theme}
                                  $active={
                                    getActiveSentTab(
                                      sentNotificationId
                                    ) === "az"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveSentTab(
                                      sentNotificationId,
                                      "az"
                                    );
                                  }}
                                >
                                  Azərbaycan
                                </TabButton>
                                <TabButton
                                  theme={theme}
                                  $active={
                                    getActiveSentTab(
                                      sentNotificationId
                                    ) === "en"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveSentTab(
                                      sentNotificationId,
                                      "en"
                                    );
                                  }}
                                >
                                  English
                                </TabButton>
                                <TabButton
                                  theme={theme}
                                  $active={
                                    getActiveSentTab(
                                      sentNotificationId
                                    ) === "ru"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveSentTab(
                                      sentNotificationId,
                                      "ru"
                                    );
                                  }}
                                >
                                  Русский
                                </TabButton>
                              </TabButtons>
                              <TabContent theme={theme}>
                                <SentNotificationTitle
                                  theme={theme}
                                >
                                  {getLocalizedText(
                                    notification?.title,
                                    getActiveSentTab(
                                      sentNotificationId
                                    )
                                  ) || "Başlıq yoxdur"}
                                </SentNotificationTitle>
                              </TabContent>
                            </LanguageTabs>
                          </div>
                          <SentNotificationTime
                            theme={theme}
                          >
                            {formatTime(sentTime)}
                          </SentNotificationTime>
                        </SentNotificationHeader>
                        <SentNotificationBody theme={theme}>
                          {getLocalizedText(
                            notification?.body,
                            getActiveSentTab(
                              sentNotificationId
                            )
                          ) || "Məzmun yoxdur"}
                        </SentNotificationBody>
                        <SentNotificationMeta theme={theme}>
                          {customer && (
                            <>
                              <SentNotificationCustomer
                                theme={theme}
                              >
                                {customer.name ||
                                  "Naməlum istifadəçi"}
                              </SentNotificationCustomer>
                              {notification?.key && (
                                <>
                                  <span>•</span>
                                  <span>
                                    {notification.key}
                                  </span>
                                </>
                              )}
                            </>
                          )}
                        </SentNotificationMeta>
                      </SentNotificationItem>
                    );
                  }
                )}
              </SentNotificationsList>
            )}
          </Section>
        </TopRowLayout>

        <Section theme={theme}>
          {loading ? (
            <LoadingContainer theme={theme}>
              Yüklənir...
            </LoadingContainer>
          ) : filteredNotifications.length === 0 ? (
            <EmptyState theme={theme}>
              <EmptyIcon>
                <NotificationsIcon
                  style={{ fontSize: "inherit" }}
                />
              </EmptyIcon>
              <p>Bildiriş tapılmadı</p>
              <Button
                onClick={() =>
                  navigate("/admin/notifications/new")
                }
                theme={theme}
                style={{ marginTop: "16px" }}
              >
                <Add /> İlk Bildirişi Yarat
              </Button>
            </EmptyState>
          ) : (
            <>
              <NotificationsGrid theme={theme}>
                {filteredNotifications.map(
                  (notification) => {
                    const notificationId =
                      notification._id || notification.id;
                    const variantColor =
                      notification.variant
                        ? getVariantColor(
                            notification.variant
                          )
                        : "#9E9E9E";

                    return (
                      <NotificationCard
                        key={notificationId}
                        theme={theme}
                        $accentColor={
                          notification.aiMode
                            ? "#667eea"
                            : variantColor
                        }
                        onClick={() =>
                          navigate(
                            `/admin/notifications/${notificationId}`
                          )
                        }
                        style={{
                          border: notification.aiMode
                            ? "2px solid #667eea"
                            : undefined,
                          boxShadow: notification.aiMode
                            ? "0 4px 12px rgba(102, 126, 234, 0.2)"
                            : undefined,
                        }}
                      >
                        <NotificationHeader>
                          {notification.icon &&
                            (() => {
                              const IconComponent =
                                Icons[notification.icon];
                              return IconComponent ? (
                                <NotificationIconContainer
                                  theme={theme}
                                >
                                  <IconComponent
                                    style={{ fontSize: 24 }}
                                  />
                                </NotificationIconContainer>
                              ) : notification.iconPath ? (
                                <NotificationIconImage
                                  src={
                                    notification.iconPath
                                  }
                                  alt="Notification Icon"
                                  theme={theme}
                                />
                              ) : null;
                            })()}
                          <NotificationInfo>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: getTheme(isDark)
                                  .spacing.xs,
                                marginBottom:
                                  getTheme(isDark).spacing
                                    .xs,
                                flexWrap: "wrap",
                              }}
                            >
                              <NotificationKey
                                theme={theme}
                              >
                                {notification.key}
                              </NotificationKey>
                              {notification.enabled ===
                                false && (
                                <DisabledBadge
                                  theme={theme}
                                >
                                  <Block
                                    style={{ fontSize: 12 }}
                                  />
                                  Disabled
                                </DisabledBadge>
                              )}
                            </div>
                            {notification.aiMode && (
                              <AIBadge theme={theme}>
                                <SmartToy
                                  style={{ fontSize: 14 }}
                                />
                                AI Mode
                                {notification.aiModel && (
                                  <AIModelBadge
                                    theme={theme}
                                  >
                                    {notification.aiModel}
                                  </AIModelBadge>
                                )}
                              </AIBadge>
                            )}
                            <LanguageTabs theme={theme}>
                              <TabButtons theme={theme}>
                                <TabButton
                                  theme={theme}
                                  $active={
                                    getActiveTab(
                                      notificationId
                                    ) === "az"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab(
                                      notificationId,
                                      "az"
                                    );
                                  }}
                                >
                                  Azərbaycan
                                </TabButton>
                                <TabButton
                                  theme={theme}
                                  $active={
                                    getActiveTab(
                                      notificationId
                                    ) === "en"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab(
                                      notificationId,
                                      "en"
                                    );
                                  }}
                                >
                                  English
                                </TabButton>
                                <TabButton
                                  theme={theme}
                                  $active={
                                    getActiveTab(
                                      notificationId
                                    ) === "ru"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab(
                                      notificationId,
                                      "ru"
                                    );
                                  }}
                                >
                                  Русский
                                </TabButton>
                              </TabButtons>
                              <TabContent theme={theme}>
                                {notification.aiMode ? (
                                  <>
                                    <TabTitle
                                      theme={theme}
                                      style={{
                                        color:
                                          getTheme(isDark)
                                            .colors
                                            .textSecondary,
                                        fontStyle: "italic",
                                        fontSize: "14px",
                                      }}
                                    >
                                      AI tərəfindən
                                      yaradılacaq
                                    </TabTitle>
                                    <TabBody
                                      theme={theme}
                                      style={{
                                        color:
                                          getTheme(isDark)
                                            .colors
                                            .textSecondary,
                                        fontStyle: "italic",
                                        fontSize: "12px",
                                      }}
                                    >
                                      yaradılacaq
                                    </TabBody>
                                  </>
                                ) : (
                                  <>
                                    <TabTitle theme={theme}>
                                      {getLocalizedText(
                                        notification.title,
                                        getActiveTab(
                                          notificationId
                                        )
                                      ) || "Başlıq yoxdur"}
                                    </TabTitle>
                                    <TabBody theme={theme}>
                                      {getLocalizedText(
                                        notification.body,
                                        getActiveTab(
                                          notificationId
                                        )
                                      ) || "Məzmun yoxdur"}
                                    </TabBody>
                                  </>
                                )}
                              </TabContent>
                            </LanguageTabs>
                            <NotificationMeta
                              theme={theme}
                              style={{
                                marginBottom:
                                  notification.aiMode &&
                                  notification.aiPrompt
                                    ? getTheme(isDark)
                                        .spacing.sm
                                    : 0,
                              }}
                            >
                              {notification.variant && (
                                <MetaBadge
                                  theme={theme}
                                  style={{
                                    backgroundColor:
                                      getVariantColor(
                                        notification.variant
                                      ) + "20",
                                    color: getVariantColor(
                                      notification.variant
                                    ),
                                  }}
                                >
                                  {getVariantLabel(
                                    notification.variant
                                  )}
                                </MetaBadge>
                              )}
                              {notification.channelTypes &&
                                notification.channelTypes
                                  .length > 0 && (
                                  <MetaBadge theme={theme}>
                                    {
                                      notification
                                        .channelTypes.length
                                    }{" "}
                                    kanal
                                  </MetaBadge>
                                )}
                            </NotificationMeta>
                            {notification.aiMode &&
                              notification.aiPrompt && (
                                <AIPromptSection
                                  theme={theme}
                                >
                                  <AIPromptHeader
                                    theme={theme}
                                    $expanded={
                                      expandedPrompts[
                                        notificationId
                                      ]
                                    }
                                    onClick={(e) =>
                                      togglePromptExpansion(
                                        notificationId,
                                        e
                                      )
                                    }
                                  >
                                    <span
                                      style={{
                                        display: "flex",
                                        alignItems:
                                          "center",
                                        gap: "6px",
                                      }}
                                    >
                                      <SmartToy
                                        style={{
                                          fontSize: 14,
                                        }}
                                      />
                                      AI Prompt
                                    </span>
                                    {expandedPrompts[
                                      notificationId
                                    ] ? (
                                      <ExpandLess
                                        style={{
                                          fontSize: 18,
                                        }}
                                      />
                                    ) : (
                                      <ExpandMore
                                        style={{
                                          fontSize: 18,
                                        }}
                                      />
                                    )}
                                  </AIPromptHeader>
                                  {expandedPrompts[
                                    notificationId
                                  ] && (
                                    <AIPromptText
                                      theme={theme}
                                    >
                                      {
                                        notification.aiPrompt
                                      }
                                    </AIPromptText>
                                  )}
                                </AIPromptSection>
                              )}
                            {notification.triggers &&
                              notification.triggers.length >
                                0 && (
                                <TriggerInfo theme={theme}>
                                  <strong>Trigger:</strong>{" "}
                                  {getTriggerSummary(
                                    notification.triggers
                                  )}
                                </TriggerInfo>
                              )}
                          </NotificationInfo>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: getTheme(isDark).spacing
                                .xs,
                            }}
                          >
                            <ToggleActions theme={theme}>
                              <ToggleButton
                                onClick={(e) =>
                                  handleToggleEnabled(
                                    notificationId,
                                    notification.enabled !==
                                      false,
                                    e
                                  )
                                }
                                theme={theme}
                                variant="outline"
                                title={
                                  notification.enabled ===
                                  false
                                    ? "Enable notification"
                                    : "Disable notification"
                                }
                              >
                                {notification.enabled ===
                                false ? (
                                  <>
                                    <ToggleOff
                                      style={{
                                        fontSize: 16,
                                        color: "#ef4444",
                                      }}
                                    />
                                    Enable
                                  </>
                                ) : (
                                  <>
                                    <ToggleOn
                                      style={{
                                        fontSize: 16,
                                        color: "#10b981",
                                      }}
                                    />
                                    Disable
                                  </>
                                )}
                              </ToggleButton>
                            </ToggleActions>
                            <NotificationActions>
                              <ActionButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/notifications/${notificationId}`
                                  );
                                }}
                                theme={theme}
                                variant="outline"
                              >
                                <Edit />
                              </ActionButton>
                              <ActionButton
                                onClick={(e) =>
                                  handleDelete(
                                    notificationId,
                                    e
                                  )
                                }
                                theme={theme}
                                variant="danger"
                              >
                                <Delete />
                              </ActionButton>
                            </NotificationActions>
                          </div>
                        </NotificationHeader>
                      </NotificationCard>
                    );
                  }
                )}
              </NotificationsGrid>
              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "8px",
                    marginTop: "16px",
                  }}
                >
                  <Button
                    theme={theme}
                    variant="outline"
                    onClick={() =>
                      setPage((p) => Math.max(1, p - 1))
                    }
                    disabled={page === 1}
                  >
                    Əvvəlki
                  </Button>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0 16px",
                      color:
                        getTheme(isDark).colors
                          .textSecondary,
                    }}
                  >
                    Səhifə {page} / {totalPages}
                  </span>
                  <Button
                    theme={theme}
                    variant="outline"
                    onClick={() =>
                      setPage((p) =>
                        Math.min(totalPages, p + 1)
                      )
                    }
                    disabled={page === totalPages}
                  >
                    Növbəti
                  </Button>
                </div>
              )}
            </>
          )}
        </Section>

        {showJobDetailsModal && selectedJob && (
          <JobDetailsModal
            job={selectedJob}
            onClose={() => {
              setShowJobDetailsModal(false);
              setSelectedJob(null);
            }}
            onRefresh={fetchJobs}
          />
        )}

        {showSimulatedTimeModal && (
          <>
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 999,
              }}
              onClick={() => {
                if (!runningSimulated) {
                  setShowSimulatedTimeModal(false);
                  setSimulatedTime("");
                  setSelectedJobName(null);
                }
              }}
            />
            <Card
              theme={theme}
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000,
                padding: getTheme(isDark).spacing.lg,
                minWidth: "400px",
                maxWidth: "90vw",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  marginBottom: getTheme(isDark).spacing.md,
                  color: getTheme(isDark).colors.text,
                }}
              >
                Simulated Time ilə İşə Sal
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: getTheme(isDark).spacing.md,
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom:
                        getTheme(isDark).spacing.xs,
                      color: getTheme(isDark).colors.text,
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    Job Name:
                  </label>
                  <input
                    type="text"
                    value={selectedJobName}
                    disabled
                    style={{
                      width: "100%",
                      padding: getTheme(isDark).spacing.sm,
                      border: `1px solid ${
                        getTheme(isDark).colors.border
                      }`,
                      borderRadius:
                        getTheme(isDark).borderRadius.md,
                      backgroundColor:
                        getTheme(isDark).colors.surface,
                      color: getTheme(isDark).colors.text,
                      fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom:
                        getTheme(isDark).spacing.xs,
                      color: getTheme(isDark).colors.text,
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    Simulated Time (HH:mm və ya ISO
                    datetime):
                  </label>
                  <input
                    type="text"
                    value={simulatedTime}
                    onChange={(e) =>
                      setSimulatedTime(e.target.value)
                    }
                    placeholder="19:00 və ya 2024-01-15T19:00:00"
                    style={{
                      width: "100%",
                      padding: getTheme(isDark).spacing.sm,
                      border: `1px solid ${
                        getTheme(isDark).colors.border
                      }`,
                      borderRadius:
                        getTheme(isDark).borderRadius.md,
                      backgroundColor:
                        getTheme(isDark).colors.surface,
                      color: getTheme(isDark).colors.text,
                      fontSize: "14px",
                    }}
                  />
                  <div
                    style={{
                      marginTop:
                        getTheme(isDark).spacing.xs,
                      fontSize: "12px",
                      color:
                        getTheme(isDark).colors
                          .textSecondary,
                    }}
                  >
                    Nümunə: 19:00 və ya 2024-01-15T19:00:00
                  </div>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom:
                        getTheme(isDark).spacing.xs,
                      color: getTheme(isDark).colors.text,
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    Timezone (İstəyə bağlı):
                  </label>
                  <select
                    value={simulatedTimezone}
                    onChange={(e) =>
                      setSimulatedTimezone(e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: getTheme(isDark).spacing.sm,
                      border: `1px solid ${
                        getTheme(isDark).colors.border
                      }`,
                      borderRadius:
                        getTheme(isDark).borderRadius.md,
                      backgroundColor:
                        getTheme(isDark).colors.surface,
                      color: getTheme(isDark).colors.text,
                      fontSize: "14px",
                    }}
                  >
                    <option value="">
                      Server Timezone (Default)
                    </option>
                    <option value="Asia/Baku">
                      Asia/Baku (Bakı)
                    </option>
                    <option value="America/New_York">
                      America/New_York (New York)
                    </option>
                    <option value="Europe/London">
                      Europe/London (London)
                    </option>
                    <option value="Asia/Dubai">
                      Asia/Dubai (Dubai)
                    </option>
                    <option value="Europe/Istanbul">
                      Europe/Istanbul (İstanbul)
                    </option>
                    <option value="Asia/Tbilisi">
                      Asia/Tbilisi (Tbilisi)
                    </option>
                    <option value="Asia/Almaty">
                      Asia/Almaty (Almatı)
                    </option>
                    <option value="Europe/Moscow">
                      Europe/Moscow (Moskva)
                    </option>
                    <option value="Asia/Tashkent">
                      Asia/Tashkent (Daşkənd)
                    </option>
                    <option value="Asia/Tehran">
                      Asia/Tehran (Tehran)
                    </option>
                    <option value="Asia/Kolkata">
                      Asia/Kolkata (Kolkata)
                    </option>
                    <option value="Asia/Tokyo">
                      Asia/Tokyo (Tokyo)
                    </option>
                    <option value="Europe/Paris">
                      Europe/Paris (Paris)
                    </option>
                    <option value="America/Los_Angeles">
                      America/Los_Angeles (Los Angeles)
                    </option>
                    <option value="America/Chicago">
                      America/Chicago (Chicago)
                    </option>
                    <option value="Australia/Sydney">
                      Australia/Sydney (Sydney)
                    </option>
                  </select>
                  <div
                    style={{
                      marginTop:
                        getTheme(isDark).spacing.xs,
                      fontSize: "12px",
                      color:
                        getTheme(isDark).colors
                          .textSecondary,
                    }}
                  >
                    Simulated time seçilən timezone-da
                    simulyasiya olunacaq
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: getTheme(isDark).spacing.sm,
                    justifyContent: "flex-end",
                    marginTop: getTheme(isDark).spacing.md,
                  }}
                >
                  <Button
                    onClick={() => {
                      setShowSimulatedTimeModal(false);
                      setSimulatedTime("");
                      setSimulatedTimezone("");
                      setSelectedJobName(null);
                    }}
                    theme={theme}
                    variant="outline"
                    disabled={runningSimulated}
                  >
                    Ləğv et
                  </Button>
                  <Button
                    onClick={handleExecuteSimulatedTime}
                    theme={theme}
                    disabled={runningSimulated}
                  >
                    {runningSimulated
                      ? "İşləyir..."
                      : "İşə Sal"}
                  </Button>
                </div>
              </div>
            </Card>
          </>
        )}
      </PageContainer>
    </MainLayout>
  );
};

export default NotificationsList;
